import { Component, Input, OnInit, SimpleChanges } from "@angular/core";
import { SharedService } from "src/app/services/shared.service";
import { CampaignService } from "src/app/services/campaign.service";
import { assetName, url } from "src/app/global/constant";

@Component({
  selector: "app-asset-pipeline-mini",
  templateUrl: "./asset-pipeline-mini.component.html",
  styleUrl: "./asset-pipeline-mini.component.scss",
  standalone: false,
})
export class AssetPipelineMiniComponent implements OnInit {
  @Input() videoId: string | number | null = null;

  campaignId: string | number | null = null;
  scriptId: string | number | null = null;
  moodboardId: string | number | null = null;
  storyboardId: string | number | null = null;
  aiVisualId: string | number | null = null;
  sonicId: string | number | null = null;
  stageStatuses: Record<number, string | null> = {};
  workspace_unique_id: string | number | null = null;
  constructor(
    private sharedService: SharedService,
    private campaignService: CampaignService,
  ) {}

  ngOnInit(): void {
    this.getVideoStages();
  }

  getVideoStages(): void {
    if (this.videoId) {
      this.campaignService.getVideoStages(this.videoId).subscribe({
        next: (res: any) => {
          this.resetStageData();
          if (res?.data?.length > 0) {
            const data = res.data;
            data.forEach((item: any) => {
              const stageIndex = this.getStageIndexByName(item?.name);
              if (!stageIndex) return;

              const assetId = item.asset_id ?? null;
              this.campaignId = item.campaign_id ?? null;
              const statusName = item?.status?.name ?? null;
              this.stageStatuses[stageIndex] = statusName;

              if (stageIndex === 1) {
                this.campaignId = this.campaignId ?? null;
              } else if (stageIndex === 2) {
                this.scriptId = assetId;
              } else if (stageIndex === 3) {
                this.moodboardId = assetId;
              } else if (stageIndex === 4) {
                this.storyboardId = assetId;
              } else if (stageIndex === 5) {
                this.aiVisualId = assetId;
              } else if (stageIndex === 6) {
                this.sonicId = assetId;
              } else if (stageIndex === 7) {
                this.workspace_unique_id = assetId;
              }
            });
          }
        },
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["videoId"]?.currentValue && !changes["videoId"]?.firstChange) {
      this.videoId = changes["videoId"].currentValue;
      this.getVideoStages();
    }
  }

  isClickable(stageIndex: number): boolean {
    if (!this.videoId) return false;
    // Keep Manifest entry always reachable for this video.
    if (stageIndex === 1) return true;
    return (
      this.hasStageStarted(stageIndex) ||
      this.isStageActive(stageIndex) ||
      this.isStageCompleted(stageIndex)
    );
  }

  hasStageStarted(stageIndex: number): boolean {
    if (this.isStageActive(stageIndex) || this.isStageCompleted(stageIndex))
      return false;
    switch (stageIndex) {
      case 1:
        return !!this.campaignId;
      case 2:
        return !!this.scriptId;
      case 3:
        return !!this.moodboardId;
      case 4:
        return !!this.storyboardId;
      case 5:
        return !!this.aiVisualId;
      case 6:
        return !!this.sonicId;
      case 7:
        return !!this.workspace_unique_id;
      default:
        return false;
    }
  }

  isStageCompleted(stageIndex: number): boolean {
    const status = (this.stageStatuses[stageIndex] || "").toLowerCase();
    return status === "approved" || status === "completed";
  }

  isStageActive(stageIndex: number): boolean {
    const status = (this.stageStatuses[stageIndex] || "").toLowerCase();
    return status === "in progress";
  }

  private getStageIndexByName(
    stageName: string | null | undefined,
  ): number | null {
    if (stageName === assetName.concept) return 1;
    if (stageName === assetName.script) return 2;
    if (stageName === assetName.moodboard) return 3;
    if (stageName === assetName.storyboard) return 4;
    if (stageName === assetName.aiVisual) return 5;
    if (stageName === assetName.sound) return 6;
    if (stageName === assetName.Preview) return 7;
    return null;
  }

  private resetStageData(): void {
    this.campaignId = null;
    this.scriptId = null;
    this.moodboardId = null;
    this.storyboardId = null;
    this.aiVisualId = null;
    this.sonicId = null;
    this.stageStatuses = {};
  }

  navigateTo(stageIndex: number): void {
    if (!this.isClickable(stageIndex)) return;

    switch (stageIndex) {
      case 1:
        this.sharedService.gotopage(
          `${url.videoDetails}?id=${this.campaignId}&videoId=${this.videoId}`,
        );
        break;
      case 2:
        this.sharedService.gotopage(
          this.scriptId
            ? `${url.script}/${this.videoId}/${this.scriptId}`
            : `${url.script}/${this.videoId}`,
        );
        break;
      case 3:
        this.sharedService.gotopage(
          this.moodboardId
            ? `${url.moodboard}${this.videoId}/${this.moodboardId}`
            : `${url.moodboard}${this.videoId}`,
        );
        break;
      case 4:
        this.sharedService.gotopage(
          this.storyboardId
            ? `${url.storyboard}${this.videoId}/${this.storyboardId}`
            : `${url.storyboardGate}${this.videoId}`,
        );
        break;
      case 5:
        this.sharedService.gotopage(
          this.aiVisualId
            ? `${url.productionVideo}${this.videoId}/${this.aiVisualId}`
            : `${url.productionVideo}${this.videoId}`,
        );
        break;
      case 6:
        this.sharedService.gotopage(
          this.sonicId
            ? `${url.sonicLayer}${this.videoId}/${this.sonicId}`
            : `${url.sonicLayer}${this.videoId}`,
        );
        break;
      case 7:
        if (this.workspace_unique_id) {
          // Workspace opens in a new tab so the user keeps the current
          // pipeline view alongside the editorial workspace.
          window.open(
            `/workspace/${this.workspace_unique_id}`,
            "_blank",
            "noopener",
          );
        } else {
          this.sharedService.gotopage(`${url.productionVideo}${this.videoId}`);
        }
        break;
    }
  }
}
